export const localeMeta = {
  en: { code: "EN", label: "English", nativeLabel: "English", region: "Global", route: "/en" },
  zh: { code: "ZH", label: "Chinese", nativeLabel: "简体中文", region: "China", route: "/zh" },
  ko: { code: "KO", label: "Korean", nativeLabel: "한국어", region: "Korea HQ", route: "/ko" },
  vi: { code: "VI", label: "Vietnamese", nativeLabel: "Tiếng Việt", region: "Vietnam", route: "/vi" },
} as const;

export type Locale = keyof typeof localeMeta;

export const defaultLocale: Locale = "en";

const baseEnglishCopy = {
  en: {
    header: {
      nav: [
        { label: "Home", href: "#home" },
        { label: "Brands", href: "#brands" },
        { label: "About", href: "#about" },
        { label: "AI Solutions", href: "#ai-solutions" },
        { label: "Network", href: "#network" },
        { label: "News", href: "#insights" },
        { label: "Contact", href: "#contact" },
      ],
      companyName: "QIANDU",
      companyCn: "千渡",
    },
    seo: {
      title: "QIANDU | Global K-Beauty Brand Partner from Korea",
      description:
        "QIANDU is the international operating brand of 주식회사 천도그룹, a Korea-registered K-Beauty brand and market-entry company connecting Korean supply with APAC execution.",
      ogTitle: "QIANDU | Global K-Beauty Brand Partner from Korea",
      ogDescription:
        "A Korea-registered K-Beauty brand, distribution, and market-entry partner connecting Korean supply with APAC execution.",
    },
    hero: {
      badge: "Korea HQ",
      companyLine: "QIANDU · 주식회사 천도그룹",
      eyebrow: "Korean beauty, global portfolio",
      title: "From Korea, K-Beauty brands and curated products for global markets.",
      intro: "Korean brands, curated products, and AI-backed market capability in one clear portfolio.",
      body: "From Korea, we connect Korean beauty brands with regional distributors and global partners.",
      primaryCta: "Explore Brands",
      secondaryCta: "View Company",
      sideTag: "Korea to APAC",
      microTag: "Built for distributors and market operators",
      visualEyebrow: "Signature Portfolio",
      visualTitle: "Korean beauty brands, presented with a calmer global point of view.",
      mapTopLeft: "Regional coverage",
      mapTopRight: "Distribution intelligence",
      heroMetrics: [
        { label: "Registered base", value: "Incheon, Korea" },
        { label: "Priority markets", value: "APAC" },
        { label: "Partner model", value: "Distribution-first" },
      ],
      credibilityMetrics: [
        { value: "Korea", label: "Headquarters and sourcing base" },
        { value: "Global", label: "Beauty market orientation" },
        { value: "Brand Portfolio", label: "Agency and signature products" },
        { value: "AI-native", label: "Intelligence operating layer" },
      ],
      nodes: [
        { city: "Incheon", left: "55%", top: "29%" },
        { city: "Ho Chi Minh City", left: "66%", top: "53%" },
        { city: "Jakarta", left: "70%", top: "66%" },
      ],
    },
    about: {
      eyebrow: "About QIANDU",
      title: "A Korea-based beauty company with a global point of view.",
      body: "Creating value for customers and growing together is the core value of QIANDU.",
      capabilityBlocks: [
        "Cross-border beauty sourcing",
        "Territory distribution structuring",
        "AI-assisted market intelligence",
        "Regional partner qualification",
      ],
      pillars: [
        {
          title: "From Trading to Infrastructure",
          body: "QIANDU is evolving beyond traditional beauty trading into a technology-enabled supply chain platform that links sourcing, intelligence, and regional channel execution.",
        },
        {
          title: "Korean HQ, Regional Execution",
          body: "With our sourcing roots in Korea and operating capability across Southeast Asia, we translate premium K-Beauty potential into localized commercial action.",
        },
        {
          title: "AI as the Operating Layer",
          body: "Our next phase is built on AI agents, data operations, and market sensing systems that help reduce friction across buyer discovery and route-to-market planning.",
        },
      ],
      transformEyebrow: "Transformation Path",
      transformTitle: "Three stages of capability evolution",
      transformBadge: "Korea to APAC",
      timeline: [
        {
          phase: "01",
          title: "Foundation",
          body: "Traditional beauty trading and supplier-side relationship building anchored in Korea.",
        },
        {
          phase: "02",
          title: "Digitization",
          body: "Structured buyer data, multilingual outreach support, and market process standardization.",
        },
        {
          phase: "03",
          title: "AI-driven Hub",
          body: "Integrated intelligence workflows for sourcing, partner validation, and cross-border channel execution.",
        },
      ],
      positioningLabel: "Current Positioning",
      positioningValue: "Global K-Beauty brand company",
      focusLabel: "Core Focus",
      focusValue: "Brands + products + AI capability",
      narrativeLabel: "Corporate Narrative",
      narrativeBody:
        "QIANDU builds value through Korean sourcing, regional channel logic, and AI-supported execution.",
    },
    businessScope: {
      eyebrow: "What We Do",
      title: "Brand, market, and execution in one operating model.",
      body: "A clear operating model for Korean beauty brands entering regional markets.",
      items: [
        {
          title: "Brand Representation",
          body: "Agency partnerships, territory-based general distribution, and structured brand rollout support for Korean beauty labels.",
        },
        {
          title: "Supply Chain Coordination",
          body: "Sourcing alignment, OEM coordination, and partner communication organized through clearer regional operating logic.",
        },
        {
          title: "Market Intelligence",
          body: "AI-assisted lead validation, trend sensing, and route-to-market analysis across APAC beauty categories.",
        },
        {
          title: "Regional Execution",
          body: "Practical execution for Southeast Asian channel expansion, with a focus on distributor fit and market-entry readiness.",
        },
      ],
      scopeLabel: "Area",
    },
    brands: {
      eyebrow: "Brand Portfolio",
      title: "Recognized Korean beauty brands, curated for partners.",
      body: "A selected portfolio for distribution and market-entry conversations.",
      stats: [
        { value: "Agency", label: "Selective brand representation" },
        { value: "Territory", label: "Exclusive market rights" },
        { value: "Global", label: "Cross-market distribution rights" },
      ],
      showcaseEyebrow: "Product Showcase",
      showcaseTitle: "Product image carousel for premium portfolio presentation",
      showcaseBody: "A product-led view of the portfolio.",
      spotlightEyebrow: "Agency Spotlight",
      spotlightTitle: "Highlighted partner brands",
      spotlightBody: "Selected brands for clear recognition across markets.",
      featuredLabel: "Featured brand",
      slides: [
        {
          title: "K-Beauty Product Storytelling",
          subtitle: "Premium skincare visuals shaped for international brand presentation.",
          image: "/brand-showcase/showcase-wide.jpg",
          accent: "Editorial hero",
        },
        {
          title: "Texture, Ritual, Shelf Appeal",
          subtitle: "Product-led visual merchandising built for premium beauty presentation.",
          image: "/brand-showcase/showcase-left.jpg",
          accent: "Retail-ready format",
        },
        {
          title: "Clinical and Lifestyle Balance",
          subtitle: "A portfolio language bridging efficacy, beauty ritual, and market fit.",
          image: "/brand-showcase/showcase-center.jpg",
          accent: "Portfolio positioning",
        },
        {
          title: "Hero Packshot Direction",
          subtitle: "Focused framing for launches, campaign headers, and featured brand pages.",
          image: "/brand-showcase/showcase-packshot.jpg",
          accent: "Campaign asset",
        },
      ],
      featuredBrands: [
        {
          name: "ROUND LAB",
          slogan: "A high-trust Korean skincare brand led by the Dokdo line.",
          category: "China General Distribution",
          relationship: "Exclusive China general distributor for Dokdo line products.",
          positioning: "Clean, calm skincare with strong recognition and clear distributor value.",
          markets: ["China", "Korea"],
        },
        {
          name: "CNP",
          slogan: "A clinical Korean skincare brand with derma-care authority.",
          category: "LG H&H Agency Partner",
          relationship: "Selective agency representation under the LG H&H portfolio.",
          positioning: "A derma-inspired skincare layer suited to high-trust retail and partner presentation.",
          markets: ["APAC", "Global"],
        },
        {
          name: "belif",
          slogan: "A globally familiar hydration brand known for Aqua Bomb care.",
          category: "LG H&H Agency Partner",
          relationship: "Selective agency representation under the LG H&H portfolio.",
          positioning: "A strong hydration story with premium retail familiarity and clean global recognition.",
          markets: ["APAC", "Global"],
        },
        {
          name: "ILSO",
          slogan: "A clean Korean skincare brand suited to Southeast Asian expansion.",
          category: "Vietnam & Myanmar Distribution",
          relationship: "Exclusive general distributor for Vietnam and Myanmar.",
          positioning: "Refined skincare positioning built for localized distribution growth.",
          markets: ["Vietnam", "Myanmar"],
        },
        {
          name: "BEYOND",
          slogan: "An LG H&H eco-beauty brand spanning skincare, body care, and hair care.",
          category: "LG H&H Agency Partner",
          relationship: "Selective agency representation under the LG H&H portfolio.",
          positioning: "A daily-care beauty brand that broadens the portfolio with eco-minded care categories.",
          markets: ["APAC", "Global"],
        },
      ],
      groups: [
        {
          title: "Agency Partners",
          summary: "Selective agency representation for established Korean beauty labels.",
          items: ["JMSOLUTION", "SNP", "THE PALACE SECRET", "CNP", "beyond", "belif", "Sungboon", "efilow", "numbuzin", "温丽慈 (selected products)"],
        },
        {
          title: "China General Distribution",
          summary: "Category-defining distribution rights in the China market.",
          items: ["Round Lab Dokdo line products"],
        },
        {
          title: "Territory General Distribution",
          summary: "Exclusive rights across specific high-growth Southeast Asian markets.",
          items: ["ILSO - Vietnam, Myanmar", "Rataplan - Thailand, Myanmar"],
        },
        {
          title: "Global General Distribution",
          summary: "Global-scale commercial authority for selected premium brands.",
          items: ["De noah", "Calvisana"],
        },
        {
          title: "Owned Brand",
          summary: "QIANDU-controlled brand asset for long-term platform growth.",
          items: ["melomeli"],
        },
      ],
      itemsLabel: "items",
    },
    intelligence: {
      eyebrow: "Intelligence Node",
      title: "AI capability for market intelligence and execution.",
      body: "QIANDU uses AI to support partner discovery, market visibility, and faster decisions.",
      nodes: [
        {
          title: "Lead Engine",
          body: "Real-time B2B lead generation and validation for distributor and wholesaler discovery.",
          stat: "24/7 active scouting",
          tags: ["Buyer Discovery", "Validation", "CRM Enrichment"],
        },
        {
          title: "Channel OS",
          body: "Market-by-market filtering for distributor fit, wholesaler screening, and route-to-market setup.",
          stat: "Faster partner routing",
          tags: ["RTM Design", "Distributor Fit", "Territory Logic"],
        },
        {
          title: "Market Radar",
          body: "Trend and policy monitoring across APAC to support better timing and market selection.",
          stat: "Cross-market signal layer",
          tags: ["Policy Watch", "Trend Sensing", "Localization"],
        },
      ],
      contextEyebrow: "Operating Context",
      contextTitle: "AI-native workflows for regional beauty execution.",
      contextParagraphs: [
        "QIANDU combines buyer data, validation logic, and market monitoring to connect Korean supply with local channel decisions.",
        "The result is a more focused expansion model with faster qualification and clearer market timing.",
      ],
      workflowLabel: "Workflow",
      workflowValue: "Lead to Channel",
      focusLabel: "Focus",
      focusValue: "APAC Beauty Markets",
      terminalTitle: "qd-m4pro-intelligence.log",
      terminalLive: "Live Stream",
      terminalLines: [
        { tone: "INFO", text: "Booting QIANDU intelligence node..." },
        { tone: "INFO", text: "Scanning for K-Beauty wholesalers in Ho Chi Minh City..." },
        { tone: "DONE", text: "142 qualified B2B entities validated." },
        { tone: "INFO", text: "Updating APAC route-to-market filters..." },
        { tone: "DONE", text: "Bangkok channel map synchronized." },
        { tone: "INFO", text: "Monitoring policy and trend signals across Indonesia..." },
        { tone: "DONE", text: "Market Radar refresh complete." },
      ],
    },
    network: {
      eyebrow: "Global Network",
      title: "An operating footprint from Korea to regional growth markets.",
      body: "Our network connects sourcing, intelligence, and regional execution from a Korean base.",
      tags: ["4 Core Nodes", "Korea + SEA"],
      activeEyebrow: "Active Node",
      logicLabel: "Regional Logic",
      logicBody: "Each node serves a distinct role, from sourcing and intelligence to regional execution.",
      nodes: [
        {
          city: "Incheon",
          coordinates: [126.7052, 37.4563] as [number, number],
          business: "Registered Headquarters",
          description: "Korean legal headquarters supporting brand partnerships, supply-chain coordination, and corporate management.",
          focus: ["Legal Entity", "Supply Chain", "Brand Partnerships"],
        },
        {
          city: "Ho Chi Minh City",
          coordinates: [106.6297, 10.8231] as [number, number],
          business: "Data Node",
          description: "Regional intelligence node supporting buyer discovery, data workflows, and multilingual market execution across Southeast Asia.",
          focus: ["Lead Intelligence", "Data Operations", "Sales Enablement"],
        },
        {
          city: "Jakarta",
          coordinates: [106.8456, -6.2088] as [number, number],
          business: "Distribution Hub",
          description: "Route-to-market activation point for Indonesia, with focus on retail channel access and local distribution readiness.",
          focus: ["Market Entry", "Retail Mapping", "Distributor Screening"],
        },
        {
          city: "Bangkok",
          coordinates: [100.5018, 13.7563] as [number, number],
          business: "Channel Intelligence",
          description: "Regional monitoring point for channel shifts, assortment strategy, and competitor movement within fast-moving beauty categories.",
          focus: ["Channel Scan", "Competitive Watch", "Assortment Insight"],
        },
      ],
    },
    operating: {
      eyebrow: "Operating Standards",
      title: "Structured like an international operating company, not a simple reseller.",
      body: "QIANDU is building a modular business system for cross-border beauty growth, combining commercial structure, local market logic, and intelligence-led execution.",
      standardLabel: "Standard",
      items: [
        {
          title: "Commercial Structuring",
          body: "We organize partnerships through clear agency, territory-distribution, and global-rights models to avoid channel ambiguity and support disciplined growth.",
          points: ["Mandate clarity", "Channel boundary design", "Partner qualification"],
        },
        {
          title: "Market Localization",
          body: "Each market is approached through localized channel logic, buyer intelligence, and category adaptation rather than simple export replication.",
          points: ["Country-specific routing", "Localized retail logic", "Market-entry prioritization"],
        },
        {
          title: "Execution Intelligence",
          body: "QIANDU combines sourcing, data, and AI-assisted monitoring to shorten decision cycles and increase confidence in regional expansion.",
          points: ["Signal monitoring", "Lead validation", "Execution feedback loops"],
        },
      ],
    },
    workflow: {
      eyebrow: "Partnership Workflow",
      title: "A clearer process for international brand collaboration and market execution.",
      body: "QIANDU's workflow is designed to reduce ambiguity from the first conversation to channel execution, especially in cross-border beauty partnerships.",
      stepLabel: "Step",
      items: [
        {
          step: "01",
          title: "Opportunity Mapping",
          body: "We identify the right market entry path, distribution model, and brand fit before activation starts.",
        },
        {
          step: "02",
          title: "Partner Qualification",
          body: "Potential distributors, wholesalers, or market partners are filtered through channel logic and commercial suitability.",
        },
        {
          step: "03",
          title: "Execution Design",
          body: "QIANDU defines product focus, route-to-market priorities, and localized operating direction for rollout.",
        },
        {
          step: "04",
          title: "Regional Launch Support",
          body: "The final phase aligns sourcing, market intelligence, and partner coordination into a measurable operating sequence.",
        },
      ],
    },
    insights: {
      eyebrow: "News",
      title: "Latest from QIANDU.",
      body: "",
      cta: "Request Full Profile",
      ready: "News module ready",
      items: [
        {
          tag: "Corporate News",
          title: "QIANDU GROUP, Wishco, and Thai celebrity PEAK completed a JMsolution offline retail activation in Bangkok.",
          body: "The activation supported local retail visibility and channel entry for JMsolution in the Bangkok beauty market.",
          date: "May 30, 2026",
          image: "/company-news/peak-jmsolution-03.jpg",
        },
        {
          tag: "Partnership Update",
          title: "Vietnamese entertainer Huong Giang was appointed promotional partner for the Korean MELOMELI cleansing water campaign.",
          body: "The collaboration expands MELOMELI brand visibility in Vietnam through a localized celebrity-led communication campaign.",
          date: "May 2026",
          image: "/company-news/melomeli-huonggiang-01.jpg",
        },
        {
          tag: "Company News",
          title: "QIANDU refines its global K-Beauty portfolio.",
          body: "The portfolio continues to evolve around distributor-ready Korean beauty brands and hero product selections.",
        },
      ],
    },
    profile: {
      eyebrow: "Company Profile",
      title: "For distributors, brand partners, and strategic conversations.",
      body: "A clear overview of QIANDU's brand portfolio, regional network, and AI-enabled operating model.",
      blocks: [
        "Corporate overview and positioning",
        "Brand portfolio and mandate structure",
        "Regional network and execution nodes",
        "AI-driven operating model and intelligence layer",
      ],
      preview: "Company File",
      documentTitle: "QIANDU Company Profile",
      pdfReady: "Available",
      paragraphs: [
        "Prepared for international beauty brands, regional distributors, and strategic partners.",
        "Includes corporate positioning, rights structure, market footprint, and the AI-enabled execution model behind QIANDU's next phase.",
      ],
      request: "Request Profile",
      talk: "Talk to QIANDU",
    },
    languages: {
      eyebrow: "Language & Market Editions",
      title: "Multi-language messaging shaped for real regional business conversations.",
      body: "Rather than translating word for word, each language edition is framed around how QIANDU would speak to different markets, partners, and commercial contexts.",
      available: "Available Languages",
      active: "Active Language",
      editionLabel: "Edition",
      messagingLabel: "Market-specific Corporate Messaging",
      focusTitle: "Localization Focus",
      releaseNote: "Recommended release step: turn this section into a full site-wide language system with dedicated localized pages, navigation, and contact variants.",
      options: [
        {
          locale: "en" as Locale,
          headline: "AI-driven beauty supply chain center connecting Korea with APAC growth.",
          body: "QIANDU integrates sourcing, brand partnership, distribution structuring, and market intelligence into a single international operating layer for beauty growth.",
          chips: ["Brand Partnership", "Distribution", "AI Intelligence"],
          microcopy: "For global brands, investors, and regional channel partners.",
          focus: ["Global positioning", "Corporate clarity", "Partner communication"],
        },
        {
          locale: "zh" as Locale,
          headline: "以韩国总部为核心，面向亚太市场的 AI 驱动美妆供应链中心。",
          body: "QIANDU 将品牌合作、选品采购、渠道布局与市场情报整合为统一的国际化运营体系，帮助品牌实现更高效的跨境增长。",
          chips: ["品牌合作", "渠道拓展", "市场情报"],
          microcopy: "适合中国市场合作方、渠道商与品牌客户阅读。",
          focus: ["渠道合作", "总代结构", "跨境增长"],
        },
        {
          locale: "ko" as Locale,
          headline: "한국 본사를 기반으로 APAC 시장을 연결하는 AI 기반 뷰티 공급망 허브.",
          body: "QIANDU는 소싱, 브랜드 파트너십, 유통 구조 설계, 시장 인텔리전스를 통합하여 글로벌 뷰티 확장을 위한 운영 레이어를 구축합니다.",
          chips: ["브랜드 파트너십", "유통 구조", "시장 인텔리전스"],
          microcopy: "한국 본사 및 브랜드 파트너 커뮤니케이션용 메시지 구조.",
          focus: ["소싱 허브", "브랜드 협업", "운영 기준"],
        },
        {
          locale: "vi" as Locale,
          headline: "Trung tâm chuỗi cung ứng làm đẹp ứng dụng AI, kết nối Hàn Quốc với tăng trưởng APAC.",
          body: "QIANDU kết hợp sourcing, hợp tác thương hiệu, cấu trúc phân phối và intelligence thị trường để tạo ra một mô hình vận hành xuyên biên giới rõ ràng hơn.",
          chips: ["Hợp tác thương hiệu", "Phân phối", "Thông tin thị trường"],
          microcopy: "Phù hợp cho đối tác phân phối, bán buôn và vận hành tại Việt Nam.",
          focus: ["Mở rộng thị trường", "Phân phối", "Tối ưu kênh"],
        },
      ],
    },
    faq: {
      eyebrow: "FAQ / Business Scope",
      title: "Common questions from international partners and distributors.",
      body: "A concise overview of how QIANDU works, where we focus, and how the operating model supports beauty brands entering or scaling across regional markets.",
      items: [
        {
          q: "What kind of brands does QIANDU work with?",
          a: "QIANDU works with Korean beauty brands across agency partnerships, territory-specific general distribution, global distribution mandates, and owned-brand development.",
        },
        {
          q: "Which markets are currently prioritized?",
          a: "The current operating footprint emphasizes Korea as sourcing headquarters and Southeast Asia as the primary regional growth zone, with structured focus on markets such as Vietnam, Myanmar, Thailand, Indonesia, and China-facing distribution rights.",
        },
        {
          q: "How does AI actually support the business?",
          a: "AI supports buyer discovery, validation, market trend sensing, route-to-market structuring, and information workflows that help shorten commercial decision cycles.",
        },
        {
          q: "Can QIANDU support OEM or product-side coordination?",
          a: "Yes. The operating model is designed to connect sourcing, OEM alignment, product positioning, and market-entry logic into one coordinated process.",
        },
      ],
    },
    contact: {
      eyebrow: "Contact & Partnership",
      title: "Connect with QIANDU for brand and market collaboration.",
      body: "For distribution, sourcing, and market-entry conversations.",
      engagementTracks: [
        "Distributor onboarding",
        "Regional market mandates",
        "Korean sourcing coordination",
        "APAC channel development",
      ],
      formTitle: "Partnership Form Preview",
      formFields: ["Company / Brand", "Contact Person", "Market of Interest", "Message"],
      responseLabel: "Response",
      responseValue: "Structured",
      coverageLabel: "Coverage",
      coverageValue: "Korea + APAC",
      fitLabel: "Fit",
      fitValue: "Brand / Channel",
      send: "Send Inquiry",
      formNext: "Web form integration can be connected next",
      directReach: "Direct Reach",
      emailBody: "For brand partnerships, channel collaboration, OEM projects, and regional market intelligence discussions across Korea and Southeast Asia.",
      facts: [
        { label: "Registered HQ", value: "Incheon, Korea" },
        { label: "Focus", value: "Beauty Supply Chain & Distribution" },
        { label: "Partnership Type", value: "Agency / Territory / Global Rights" },
      ],
      start: "Start a Conversation",
      viewNetwork: "View Network",
      routingLabel: "Partnership routing",
      routingBody:
        "Best suited for brand mandates, distribution partnerships, OEM projects, and intelligence-led market entry conversations.",
      footnote: "Structured response path for brand, distribution, sourcing, and market-entry discussions",
    },
    footer: {
      summary: "Creating value for customers and growing together is the core value of QIANDU.",
      pill: "Korea HQ · Global Markets · Tech-Fused Beauty",
      quickLinksTitle: "Quick Links",
      languageTitle: "Language",
      languageLine1: "English / 中文 / 한국어 / Tiếng Việt",
      languageLine2: "Site-wide language switcher",
      contactTitle: "Contact",
      contactLocation: "Incheon registered headquarters\nKorea and APAC operations",
      requestProfile: "Request Company Profile",
      copyright: "© 2026 QIANDU. All Rights Reserved.",
      tag: "Create Value, Grow Together",
    },
  },
} as const;

const localeOverrides: Record<Exclude<Locale, "en">, any> = {
  zh: {
    header: {
      nav: [
        { label: "首页", href: "#home" },
        { label: "品牌矩阵", href: "#brands" },
        { label: "关于我们", href: "#about" },
        { label: "AI 解决方案", href: "#ai-solutions" },
        { label: "全球网络", href: "#network" },
        { label: "新闻动态", href: "#insights" },
        { label: "联系我们", href: "#contact" },
      ],
    },
    seo: {
      title: "QIANDU 千渡 | 立足韩国 面向全球的美妆护肤品牌公司",
      description:
        "QIANDU 千渡是一家立足韩国、面向全球的美妆护肤品牌公司，展示代理品牌、核心产品、AI 能力与公司新闻动态。",
      ogTitle: "QIANDU 千渡 | 立足韩国 面向全球的美妆护肤品牌公司",
      ogDescription: "展示韩国美妆护肤品牌形象、代理产品、AI 能力与公司动态。",
    },
    hero: {
      eyebrow: "韩国美妆 · 全球市场",
      title: "韩国美妆品牌与精选产品，面向全球合作伙伴。",
      intro: "立足韩国，连接品牌、产品与区域渠道合作。",
      body: "以更清晰的品牌呈现与市场能力，服务代理商、分销商与国际合作伙伴。",
      primaryCta: "查看品牌与产品",
      secondaryCta: "了解公司",
      sideTag: "从韩国到 APAC",
      microTag: "服务代理商、分销商与区域合作方",
      visualEyebrow: "核心品牌组合",
      visualTitle: "以更清晰、更克制的方式呈现韩国美妆品牌。",
      mapTopLeft: "覆盖区域",
      mapTopRight: "分销情报支持",
      heroMetrics: [
        { label: "源头基础", value: "韩国总部" },
        { label: "重点市场", value: "APAC" },
        { label: "合作模式", value: "分销优先" },
      ],
      credibilityMetrics: [
        { value: "韩国", label: "总部与品牌源头" },
        { value: "全球", label: "市场面向与增长视角" },
        { value: "品牌组合", label: "代理品牌与核心产品" },
        { value: "AI 驱动", label: "市场情报与运营能力" },
      ],
      nodes: [
        { city: "仁川", left: "55%", top: "29%" },
        { city: "胡志明市", left: "66%", top: "53%" },
        { city: "雅加达", left: "70%", top: "66%" },
      ],
    },
    about: {
      eyebrow: "关于 QIANDU",
      title: "一家立足韩国、面向全球的美妆品牌公司。",
      body: "为客户创造价值，并与客户共同成长，是千渡的核心价值观。",
      capabilityBlocks: ["韩国品牌资源对接", "区域总代结构设计", "分销商匹配与筛选", "市场进入支持"],
      pillars: [
        { title: "从贸易走向基础设施", body: "QIANDU 正从传统美妆贸易公司升级为技术驱动的供应链平台，连接采购、情报与区域渠道执行。" },
        { title: "韩国总部，区域落地", body: "依托韩国源头能力与东南亚运营能力，我们把优质韩妆的潜力转化为本地化商业行动。" },
        { title: "以 AI 作为运营层", body: "公司的下一阶段建立在 AI Agent、数据运营与市场感知系统之上，以降低买家发现和渠道规划过程中的摩擦。" },
      ],
      transformEyebrow: "转型路径",
      transformTitle: "能力演进的三个阶段",
      transformBadge: "从韩国走向 APAC",
      timeline: [
        { phase: "01", title: "基础阶段", body: "以韩国为锚点，建立传统美妆贸易能力与供应商关系。" },
        { phase: "02", title: "数字化阶段", body: "沉淀买家数据、支持多语言触达，并推动市场流程标准化。" },
        { phase: "03", title: "AI 驱动枢纽", body: "形成覆盖采购、伙伴验证与跨境渠道执行的一体化智能工作流。" },
      ],
      positioningLabel: "当前定位",
      positioningValue: "全球化韩妆品牌公司",
      focusLabel: "核心方向",
      focusValue: "品牌形象 + 产品展示 + AI 能力",
      narrativeLabel: "企业叙事",
      narrativeBody: "QIANDU 正在演进为服务美妆品牌的区域运营平台，以采购能力、渠道逻辑与智能执行持续创造价值。",
    },
    businessScope: {
      eyebrow: "业务布局",
      title: "围绕品牌、市场与执行，建立清晰结构。",
      body: "以清晰的运营结构，服务韩国美妆品牌进入区域市场。",
      items: [
        { title: "品牌代理与授权", body: "为韩国美妆品牌提供代理合作、区域总代以及更有组织的品牌落地支持。" },
        { title: "供应链协同", body: "围绕采购对接、OEM 协同与合作伙伴沟通，形成更清晰的区域运营逻辑。" },
        { title: "市场情报", body: "面向 APAC 美妆品类的 AI 辅助线索验证、趋势监测与渠道分析。" },
        { title: "区域执行", body: "面向东南亚渠道扩张的实务执行，强调分销匹配度与市场进入准备。" },
      ],
      scopeLabel: "领域",
    },
    brands: {
      eyebrow: "品牌组合",
      title: "韩国美妆品牌组合，面向全球合作伙伴。",
      body: "精选韩国护肤与彩妆品牌，用于代理与分销合作。",
      stats: [
        { value: "代理", label: "精选品牌代理合作" },
        { value: "区域", label: "市场独家分销权" },
        { value: "全球", label: "跨区域分销合作" },
      ],
      showcaseEyebrow: "品牌预览",
      showcaseTitle: "先看产品，再理解品牌方向",
      showcaseBody: "通过产品画面，更直观地理解品牌组合。",
      spotlightEyebrow: "重点品牌",
      spotlightTitle: "重点合作品牌",
      spotlightBody: "优先展示当前重点合作品牌。",
      featuredLabel: "重点品牌",
      itemsLabel: "项",
    },
    intelligence: {
      eyebrow: "AI 能力",
      title: "服务全球市场拓展的 AI 能力",
      body: "以 AI 支持市场洞察、合作筛选与区域执行。",
      nodes: [
        { title: "Lead Engine", body: "帮助识别并验证潜在分销商、批发商与渠道合作对象。", stat: "7x24 持续扫描", tags: ["渠道发现", "合作方验证", "数据补全"] },
        { title: "Channel OS", body: "支持不同市场下的渠道设计、分销商筛选与市场进入逻辑。", stat: "更快的渠道匹配", tags: ["渠道路径", "分销商匹配", "区域逻辑"] },
        { title: "Market Radar", body: "持续跟踪 APAC 市场趋势与政策变化，帮助判断进入时机。", stat: "跨市场信号层", tags: ["政策监控", "趋势感知", "市场判断"] },
      ],
      contextEyebrow: "运营背景",
      contextTitle: "面向区域执行的 AI 工作流",
      contextParagraphs: [
        "QIANDU 结合结构化买家数据库、Agent 验证逻辑与市场监测能力，缩短韩国供给与本地渠道决策之间的距离。",
        "最终形成更有纪律的扩张模型，减少盲目触达，提高分销商筛选质量，并更快适应市场变化。",
      ],
      workflowLabel: "工作流",
      workflowValue: "从线索到渠道",
      focusLabel: "聚焦市场",
      focusValue: "APAC 美妆市场",
      terminalLive: "实时运行",
      terminalLines: [
        { tone: "INFO", text: "正在启动 QIANDU 智能节点..." },
        { tone: "INFO", text: "正在扫描胡志明市韩妆批发渠道..." },
        { tone: "DONE", text: "已验证 142 个高质量 B2B 实体。" },
        { tone: "INFO", text: "正在更新 APAC 渠道路由过滤器..." },
        { tone: "DONE", text: "曼谷渠道地图已同步。" },
        { tone: "INFO", text: "正在监测印尼政策与趋势信号..." },
        { tone: "DONE", text: "Market Radar 刷新完成。" },
      ],
    },
    network: {
      eyebrow: "全球网络",
      title: "以韩国为中心，连接区域市场与全球增长。",
      body: "连接韩国源头能力与区域市场节点，支撑品牌、产品与商业拓展。",
      tags: ["4 个核心节点", "韩国 + 东南亚"],
      activeEyebrow: "当前节点",
      logicLabel: "区域逻辑",
      logicBody: "每个节点都围绕不同的运营角色设计，让网络能够以更清晰的责任划分和更强的本地语境，从产品发现走向市场执行。",
      nodes: [
        { city: "仁川", coordinates: [126.7052, 37.4563] as [number, number], business: "注册总部", description: "韩国法定总部与登记经营场所，支持品牌合作、供应链协调与公司管理。", focus: ["法人主体", "供应链协同", "品牌合作"] },
        { city: "胡志明市", coordinates: [106.6297, 10.8231] as [number, number], business: "数据节点", description: "面向东南亚的区域情报节点，支持买家发现、数据工作流与多语言市场执行。", focus: ["线索情报", "数据运营", "销售支持"] },
        { city: "雅加达", coordinates: [106.8456, -6.2088] as [number, number], business: "分销枢纽", description: "面向印尼市场的渠道启动点，聚焦零售通路与本地分销准备。", focus: ["市场进入", "零售映射", "分销筛选"] },
        { city: "曼谷", coordinates: [100.5018, 13.7563] as [number, number], business: "渠道情报", description: "用于监测渠道变化、品类结构与竞品动态的区域观察点。", focus: ["渠道扫描", "竞品监控", "品类洞察"] },
      ],
    },
    operating: {
      eyebrow: "运营标准",
      title: "更像一家国际化运营公司，而不是单纯经销商。",
      body: "QIANDU 正在构建模块化的跨境美妆增长体系，把商业结构、本地市场逻辑与情报驱动执行组合起来。",
      standardLabel: "标准",
      items: [
        { title: "商业结构", body: "通过清晰的代理、区域总代与全球授权模式来组织合作关系，避免渠道混乱。", points: ["授权清晰", "渠道边界设计", "合作方筛选"] },
        { title: "市场本地化", body: "每个市场都以本地化渠道逻辑、买家情报与品类适配为基础，而不是简单复制出口方案。", points: ["国家级路径", "本地零售逻辑", "市场优先级"] },
        { title: "执行智能", body: "QIANDU 将采购、数据与 AI 辅助监控结合起来，缩短决策周期并提升区域扩张确定性。", points: ["信号监测", "线索验证", "执行反馈闭环"] },
      ],
    },
    workflow: {
      eyebrow: "合作流程",
      title: "更清晰的国际品牌合作与市场执行流程。",
      body: "QIANDU 的流程设计旨在减少从初次沟通到渠道执行之间的不确定性，尤其适用于跨境美妆合作。",
      stepLabel: "步骤",
      items: [
        { step: "01", title: "机会识别", body: "在项目启动前确认合适的市场进入路径、分销模型与品牌匹配度。" },
        { step: "02", title: "伙伴筛选", body: "通过渠道逻辑与商业适配度，对潜在分销商、批发商与合作方进行过滤。" },
        { step: "03", title: "执行设计", body: "明确产品重点、渠道优先级与本地化运营方向。" },
        { step: "04", title: "区域启动支持", body: "把采购、市场情报与伙伴协同整合为可衡量的执行序列。" },
      ],
    },
    insights: {
      eyebrow: "动态",
      title: "QIANDU 动态",
      body: "",
      cta: "索取完整资料",
      ready: "新闻模块已就绪",
      items: [
        {
          tag: "公司新闻",
          title: "2026年5月30日，QIANDU GROUP 联合 Wishco 与泰国明星 PEAK 在曼谷完成 JMsolution 线下零售推广活动，支持品牌进入当地美妆连锁渠道。",
          body: "该活动围绕线下零售推广与门店曝光展开，进一步提升了 JMsolution 在曼谷市场的渠道可见度。",
          date: "2026年5月30日",
          image: "/company-news/peak-jmsolution-03.jpg",
        },
        {
          tag: "合作动态",
          title: "越南艺人 Huong Giang 正式成为韩国 MELOMELI 卸妆水推广宣传合作伙伴，支持品牌在越南市场的传播与曝光。",
          body: "本次合作将围绕本地化内容传播与明星合作推广，强化 MELOMELI 在越南市场的品牌认知。",
          date: "2026年5月",
          image: "/company-news/melomeli-huonggiang-01.jpg",
        },
        { tag: "公司", title: "韩国美妆品牌组合持续升级", body: "QIANDU 持续完善重点品牌与核心产品组合，以支持国际市场的渠道合作与品牌展示。" },
      ],
    },
    profile: {
      eyebrow: "公司资料",
      title: "面向分销商、品牌伙伴与战略合作。",
      body: "清晰呈现 QIANDU 的品牌组合、区域网络与 AI 运营能力。",
      blocks: ["公司概览与定位", "品牌组合与授权结构", "区域网络与执行节点", "AI 驱动的运营与情报层"],
      preview: "公司资料",
      documentTitle: "QIANDU 公司资料",
      pdfReady: "可提供",
      paragraphs: ["适用于国际美妆品牌、区域分销商与战略合作伙伴。", "包含公司定位、授权结构、市场版图以及 QIANDU 下一阶段的 AI 执行模型。"],
      request: "索取资料",
      talk: "联系 QIANDU",
    },
    languages: {
      eyebrow: "语言与市场版本",
      title: "围绕真实区域商业沟通而构建的多语言表达。",
      body: "这不是逐字翻译，而是根据不同市场、合作对象与商业语境来调整 QIANDU 的表达方式。",
      available: "可切换语言",
      active: "当前语言",
      editionLabel: "版本",
      messagingLabel: "面向市场的企业表达",
      focusTitle: "本地化重点",
      releaseNote: "围绕不同市场语境，持续优化整站表达。",
    },
    faq: {
      eyebrow: "常见问题 / 业务说明",
      title: "国际合作伙伴与分销商常见问题。",
      body: "帮助合作方快速理解 QIANDU 的工作方式、核心市场以及运营模型如何支持区域增长。",
      items: [
        { q: "QIANDU 主要与哪些品牌合作？", a: "QIANDU 与韩国美妆品牌展开代理合作、区域总代、全球总代以及自有品牌开发等多种合作模式。" },
        { q: "当前重点市场有哪些？", a: "现阶段以韩国为采购总部、东南亚为主要增长区域，并重点关注越南、缅甸、泰国、印尼以及面向中国市场的分销机会。" },
        { q: "AI 在业务中具体如何发挥作用？", a: "AI 主要支持买家发现、线索验证、趋势感知、渠道设计和信息流管理，以缩短商业决策周期。" },
        { q: "QIANDU 是否支持 OEM 或产品协同？", a: "支持。我们的运营模型能够把采购、OEM 对接、产品定位与市场进入逻辑整合在一个协同流程里。" },
      ],
    },
    contact: {
      eyebrow: "联系与合作",
      title: "与 QIANDU 开启合作沟通。",
      body: "用于品牌合作、产品咨询与区域市场交流。",
      engagementTracks: ["代理合作洽谈", "区域分销授权", "韩国品牌资源对接", "APAC 市场落地支持"],
      formTitle: "合作咨询",
      formFields: ["公司 / 品牌", "联系人", "目标市场", "留言"],
      responseLabel: "响应方式",
      responseValue: "结构化",
      coverageLabel: "覆盖范围",
      coverageValue: "韩国 + APAC",
      fitLabel: "适配对象",
      fitValue: "品牌 / 渠道",
      send: "发送咨询",
      formNext: "欢迎通过邮件联系",
      directReach: "直接联系",
      emailBody: "适用于品牌合作、渠道协作、OEM 项目以及韩国与东南亚市场情报沟通。",
      facts: [
        { label: "注册总部", value: "韩国仁川" },
        { label: "方向", value: "美妆供应链与分销" },
        { label: "合作类型", value: "代理 / 区域总代 / 全球总代" },
      ],
      start: "开始联系",
      viewNetwork: "查看网络",
      routingLabel: "合作路由",
      routingBody: "适用于品牌授权、分销合作、OEM 项目以及以情报驱动的市场进入沟通。",
      footnote: "适用于品牌、分销、采购与市场进入等合作讨论的结构化响应路径",
    },
    footer: {
      summary: "为客户创造价值，并一起成长，是我们千渡的核心价值观。",
      pill: "韩国总部 · 全球市场 · 科技美妆",
      quickLinksTitle: "快速入口",
      languageTitle: "语言",
      languageLine1: "English / 中文 / 한국어 / Tiếng Việt",
      languageLine2: "整站语言切换",
      contactTitle: "联系",
      contactLocation: "韩国仁川注册总部\n韩国与 APAC 运营",
      requestProfile: "索取公司资料",
      copyright: "© 2026 QIANDU. 版权所有。",
      tag: "创造价值，共同成长",
    },
  },
  ko: {
    header: { nav: [{ label: "홈", href: "#home" }, { label: "브랜드", href: "#brands" }, { label: "회사 소개", href: "#about" }, { label: "AI 솔루션", href: "#ai-solutions" }, { label: "네트워크", href: "#network" }, { label: "언어", href: "#languages" }, { label: "문의", href: "#contact" }] },
    seo: {
      title: "QIANDU | AI 기반 뷰티 공급망 센터",
      description:
        "QIANDU는 한국 소싱, 시장 인텔리전스, APAC 유통 실행을 연결하는 AI 기반 뷰티 공급망 센터입니다.",
      ogTitle: "QIANDU | AI 기반 뷰티 공급망 센터",
      ogDescription: "프리미엄 K-Beauty를 소싱, 인텔리전스, 지역 실행으로 글로벌 성장 시장과 연결합니다.",
    },
    hero: {
      eyebrow: "한국 기반 글로벌 뷰티 기업",
      title: "한국 뷰티 브랜드와 제품을 글로벌 시장에 소개합니다.",
      intro: "프리미엄 K-Beauty 브랜드, 제품, AI 역량을 함께 소개합니다.",
      body: "한국 본사를 기반으로 브랜드, 제품, 지역 성장을 연결합니다.",
      primaryCta: "브랜드 보기",
      secondaryCta: "회사 소개 보기",
      sideTag: "Korea to APAC",
      microTag: "International beauty growth platform",
      visualEyebrow: "시그니처 포트폴리오",
      visualTitle: "더 정제된 글로벌 시선으로 한국 뷰티를 소개합니다.",
      mapTopLeft: "Live regional visibility",
      mapTopRight: "Beauty intelligence map",
      heroMetrics: [{ label: "한국 법인 등록지", value: "인천 본점" }, { label: "지역 인텔리전스", value: "APAC 연동" }, { label: "운영 모델", value: "AI + 공급망" }],
      credibilityMetrics: [{ value: "한국", label: "본사 및 공급 원점" }, { value: "APAC", label: "지역 실행 범위" }, { value: "AI", label: "운영 모델 전환" }, { value: "다중 권한", label: "에이전시, 지역 총판, 글로벌 권한" }],
      nodes: [{ city: "Incheon", left: "55%", top: "29%" }, { city: "Ho Chi Minh City", left: "66%", top: "53%" }, { city: "Jakarta", left: "70%", top: "66%" }],
    },
    about: {
      eyebrow: "QIANDU 소개",
      title: "한국을 기반으로 글로벌 뷰티 성장을 전개합니다.",
      body: "고객에게 가치를 만들고 함께 성장하는 것, 그것이 QIANDU의 핵심 가치입니다.",
      capabilityBlocks: ["크로스보더 뷰티 소싱", "권역 총판 구조화", "AI 기반 시장 인텔리전스", "지역 파트너 검증"],
    },
    businessScope: { eyebrow: "사업 범위", title: "브랜드, 시장, 실행 레이어 전반에서 QIANDU가 제공하는 가치.", body: "제품, 상업 관계, 인텔리전스 기반 실행을 하나의 크로스보더 성장 모델로 연결합니다.", scopeLabel: "영역" },
    brands: { eyebrow: "브랜드 포트폴리오", title: "글로벌 파트너를 위한 K-Beauty 포트폴리오.", body: "에이전시와 유통 협업을 위한 한국 뷰티 브랜드를 선별해 소개합니다.", stats: [{ value: "에이전시", label: "선별 대표 모델" }, { value: "권역", label: "독점 시장 유통" }, { value: "글로벌", label: "글로벌 총판 권한" }], showcaseEyebrow: "제품 쇼케이스", showcaseTitle: "프리미엄 포트폴리오를 위한 제품 비주얼 캐러셀", showcaseBody: "제품 중심으로 포트폴리오를 직관적으로 보여줍니다.", spotlightEyebrow: "파트너 스포트라이트", spotlightTitle: "주요 파트너 브랜드", spotlightBody: "현재 핵심 파트너 브랜드를 우선 소개합니다.", featuredLabel: "주요 브랜드", itemsLabel: "항목" },
    intelligence: { eyebrow: "AI 인텔리전스", title: "시장 실행을 돕는 AI 인텔리전스.", body: "바이어 발굴, 시장 진입 설계, 지역 의사결정을 지원합니다.", contextEyebrow: "운영 맥락", contextTitle: "지역 뷰티 실행을 위한 AI 기반 워크플로우.", workflowLabel: "흐름", workflowValue: "리드에서 채널까지", focusLabel: "중점 시장", focusValue: "APAC 뷰티 시장", terminalLive: "실시간", terminalLines: [{ tone: "INFO", text: "QIANDU 인텔리전스 노드를 시작합니다..." }, { tone: "INFO", text: "호치민시 K-Beauty 도매 채널을 스캔 중..." }, { tone: "DONE", text: "142개의 적격 B2B 엔티티 검증 완료." }, { tone: "INFO", text: "APAC RTM 필터를 업데이트 중..." }, { tone: "DONE", text: "방콕 채널 맵 동기화 완료." }, { tone: "INFO", text: "인도네시아 정책 및 트렌드 신호 모니터링 중..." }, { tone: "DONE", text: "Market Radar 새로고침 완료." }] },
    network: { eyebrow: "글로벌 네트워크", title: "한국과 동남아를 잇는 운영 네트워크.", body: "소싱, 인텔리전스, 유통 실행을 지역별 노드로 운영합니다.", tags: ["4개 핵심 거점", "한국 + 동남아"], activeEyebrow: "현재 노드", logicLabel: "지역 운영 로직", logicBody: "각 노드는 서로 다른 운영 역할을 맡아 더 명확한 책임과 로컬 맥락을 제공합니다." },
    operating: { eyebrow: "운영 기준", title: "단순 리셀러가 아닌 국제 운영 회사의 방식.", body: "상업 구조, 현지 시장 논리, 인텔리전스 기반 실행을 결합한 모듈형 시스템을 구축합니다.", standardLabel: "기준" },
    workflow: { eyebrow: "협업 프로세스", title: "국제 브랜드 협업과 시장 실행을 위한 더 명확한 프로세스.", body: "초기 대화부터 채널 실행까지의 불확실성을 줄이기 위한 워크플로우입니다.", stepLabel: "단계" },
    insights: {
      eyebrow: "소식",
      title: "QIANDU 소식",
      body: "",
      cta: "프로필 요청",
      ready: "소식 모듈 준비 완료",
      items: [
        {
          tag: "기업 소식",
          title: "2026년 5월 30일, QIANDU GROUP은 Wishco 및 태국 셀럽 PEAK과 함께 방콕에서 JMsolution 오프라인 리테일 프로모션을 진행했습니다.",
          body: "이번 프로모션은 방콕 뷰티 리테일 채널에서 JMsolution의 오프라인 가시성과 유통 접점을 강화하기 위한 활동입니다.",
          date: "2026년 5월 30일",
          image: "/company-news/peak-jmsolution-03.jpg",
        },
        {
          tag: "협업 소식",
          title: "베트남 엔터테이너 Huong Giang이 한국 MELOMELI 클렌징 워터 캠페인의 홍보 파트너로 공식 합류했습니다.",
          body: "이번 협업은 현지화된 셀럽 마케팅을 통해 베트남 시장에서 MELOMELI의 브랜드 인지도를 확대하기 위한 것입니다.",
          date: "2026년 5월",
          image: "/company-news/melomeli-huonggiang-01.jpg",
        },
        { tag: "회사", title: "K-뷰티 포트폴리오를 계속 확장하고 있습니다.", body: "QIANDU는 핵심 브랜드와 대표 제품 구성을 지속적으로 정비하며 글로벌 파트너 협업 기반을 강화하고 있습니다." },
      ],
    },
    profile: { eyebrow: "회사 소개", title: "유통사, 브랜드 파트너, 투자자를 위한 프레젠테이션 레이어.", body: "다운로드 가능한 회사 소개서나 상업용 덱으로 확장 가능한 구조입니다.", preview: "회사 자료", documentTitle: "QIANDU 회사 소개서", pdfReady: "제공 가능", request: "프로필 요청", talk: "QIANDU와 상담" },
    languages: { eyebrow: "언어 및 시장 버전", title: "실제 지역 비즈니스 대화를 위한 다국어 메시지 구조.", body: "직역이 아니라 시장과 파트너 유형에 맞춰 메시지를 재구성합니다.", available: "사용 가능한 언어", active: "현재 언어", editionLabel: "버전", messagingLabel: "시장 맞춤형 기업 메시지", focusTitle: "로컬라이제이션 포인트", releaseNote: "시장별 메시지를 더 자연스럽게 다듬고 있습니다." },
    faq: { eyebrow: "FAQ / Business Scope", title: "국제 파트너와 유통사가 자주 묻는 질문.", body: "QIANDU의 방식과 핵심 시장, 운영 모델을 간결하게 설명합니다." },
    contact: { eyebrow: "문의 및 협업", title: "QIANDU와 함께 연결하세요.", body: "브랜드 협업, 제품 문의, 시장 논의를 위한 연락 창구입니다.", formTitle: "협업 문의 폼 미리보기", responseLabel: "응답 방식", responseValue: "구조화된 응대", coverageLabel: "대응 범위", coverageValue: "한국 및 APAC", fitLabel: "적합 대상", fitValue: "브랜드 / 채널", send: "문의 보내기", formNext: "이메일로 바로 문의하실 수 있습니다.", directReach: "직접 문의", start: "대화 시작", viewNetwork: "네트워크 보기", routingLabel: "협업 라우팅", routingBody: "브랜드 권한 협업, 유통 파트너십, OEM 프로젝트, 인텔리전스 기반 시장 진입 논의에 적합합니다.", footnote: "브랜드, 유통, 소싱, 시장 진입 논의를 위한 구조화된 응답 경로" },
    footer: { summary: "고객에게 가치를 만들고 함께 성장하는 것, 그것이 QIANDU의 핵심 가치입니다.", pill: "인천 등록 본점 · APAC 운영 · 테크 뷰티", quickLinksTitle: "바로가기", languageTitle: "언어", languageLine1: "English / 中文 / 한국어 / Tiếng Việt", languageLine2: "전역 사이트 언어 전환", contactTitle: "문의", contactLocation: "대한민국 인천 등록 본점\n한국 및 APAC 운영", requestProfile: "회사 소개서 요청", copyright: "© 2026 QIANDU. 모든 권리 보유.", tag: "가치를 만들고 함께 성장합니다" },
  },
  vi: {
    header: { nav: [{ label: "Trang chủ", href: "#home" }, { label: "Thương hiệu", href: "#brands" }, { label: "Giới thiệu", href: "#about" }, { label: "Giải pháp AI", href: "#ai-solutions" }, { label: "Mạng lưới", href: "#network" }, { label: "Ngôn ngữ", href: "#languages" }, { label: "Liên hệ", href: "#contact" }] },
    seo: {
      title: "QIANDU | Trung tâm chuỗi cung ứng mỹ phẩm ứng dụng AI",
      description:
        "QIANDU là trung tâm chuỗi cung ứng mỹ phẩm ứng dụng AI, kết nối sourcing tại Hàn Quốc, intelligence thị trường và triển khai phân phối tại APAC.",
      ogTitle: "QIANDU | Trung tâm chuỗi cung ứng mỹ phẩm ứng dụng AI",
      ogDescription: "Kết nối K-Beauty cao cấp với tăng trưởng toàn cầu qua sourcing, intelligence và thực thi khu vực.",
    },
    hero: { eyebrow: "Công ty mỹ phẩm Hàn Quốc cho thị trường toàn cầu", title: "Thương hiệu và sản phẩm mỹ phẩm Hàn Quốc cho đối tác toàn cầu.", intro: "QIANDU giới thiệu thương hiệu, sản phẩm và năng lực AI của K-Beauty.", body: "Từ Hàn Quốc, chúng tôi kết nối thương hiệu, sản phẩm và tăng trưởng khu vực.", primaryCta: "Khám phá thương hiệu", secondaryCta: "Xem câu chuyện công ty", sideTag: "Từ Hàn Quốc đến APAC", microTag: "Nền tảng tăng trưởng mỹ phẩm quốc tế", visualEyebrow: "Danh mục chủ lực", visualTitle: "Mỹ phẩm Hàn Quốc được trình bày theo cách sáng sủa và hiện đại hơn cho thị trường toàn cầu.", mapTopLeft: "Hiển thị khu vực theo thời gian thực", mapTopRight: "Bản đồ intelligence mỹ phẩm", heroMetrics: [{ label: "Trụ sở đăng ký", value: "Incheon, Hàn Quốc" }, { label: "Intelligence khu vực", value: "APAC Live" }, { label: "Mô hình vận hành", value: "AI + Supply Chain" }], credibilityMetrics: [{ value: "Hàn Quốc", label: "Trụ sở và nguồn cung" }, { value: "APAC", label: "Dấu chân vận hành khu vực" }, { value: "AI", label: "Chuyển đổi mô hình vận hành" }, { value: "Đa quyền", label: "Agency, lãnh thổ và quyền phân phối toàn cầu" }], nodes: [{ city: "Incheon", left: "55%", top: "29%" }, { city: "Ho Chi Minh City", left: "66%", top: "53%" }, { city: "Jakarta", left: "70%", top: "66%" }] },
    about: { eyebrow: "Về QIANDU", title: "Một công ty mỹ phẩm Hàn Quốc với góc nhìn toàn cầu.", body: "Tạo ra giá trị cho khách hàng và cùng phát triển là giá trị cốt lõi của QIANDU.", capabilityBlocks: ["Sourcing mỹ phẩm xuyên biên giới", "Cấu trúc tổng phân phối theo lãnh thổ", "Intelligence thị trường hỗ trợ AI", "Đánh giá đối tác khu vực"] },
    businessScope: { eyebrow: "Phạm vi kinh doanh", title: "Những gì QIANDU cung cấp trên các lớp thương hiệu, thị trường và thực thi.", body: "Công ty được thiết kế để kết nối sản phẩm mỹ phẩm, quan hệ thương mại và thực thi dựa trên intelligence thành một mô hình tăng trưởng xuyên biên giới.", scopeLabel: "Phạm vi" },
    brands: { eyebrow: "Danh mục thương hiệu", title: "Danh mục K-Beauty dành cho đối tác toàn cầu.", body: "Các thương hiệu mỹ phẩm Hàn Quốc được chọn lọc cho hợp tác đại lý và phân phối.", stats: [{ value: "Đại diện", label: "Mô hình đại diện chọn lọc" }, { value: "Khu vực", label: "Phân phối độc quyền theo thị trường" }, { value: "Toàn cầu", label: "Quyền phân phối toàn cầu" }], showcaseEyebrow: "Trưng bày sản phẩm", showcaseTitle: "Carousel hình ảnh sản phẩm cho trình bày danh mục cao cấp", showcaseBody: "Một góc nhìn trực quan hơn về danh mục sản phẩm.", spotlightEyebrow: "Điểm nhấn đối tác", spotlightTitle: "Các thương hiệu đối tác được nhấn mạnh", spotlightBody: "Ưu tiên giới thiệu các thương hiệu hợp tác trọng tâm hiện tại.", featuredLabel: "Thương hiệu trọng tâm", itemsLabel: "mục" },
    intelligence: { eyebrow: "Năng lực AI", title: "Năng lực AI cho intelligence và thực thi thị trường.", body: "Hỗ trợ buyer discovery, route-to-market và quyết định theo khu vực.", contextEyebrow: "Bối cảnh vận hành", contextTitle: "Quy trình AI cho triển khai mỹ phẩm khu vực.", workflowLabel: "Quy trình", workflowValue: "Từ lead đến kênh phân phối", focusLabel: "Trọng tâm", focusValue: "Thị trường mỹ phẩm APAC", terminalLive: "Trực tuyến", terminalLines: [{ tone: "INFO", text: "Đang khởi động intelligence node của QIANDU..." }, { tone: "INFO", text: "Đang quét các wholesaler K-Beauty tại TP.HCM..." }, { tone: "DONE", text: "Đã xác thực 142 thực thể B2B phù hợp." }, { tone: "INFO", text: "Đang cập nhật bộ lọc route-to-market APAC..." }, { tone: "DONE", text: "Bản đồ kênh Bangkok đã đồng bộ." }, { tone: "INFO", text: "Đang theo dõi tín hiệu chính sách và xu hướng tại Indonesia..." }, { tone: "DONE", text: "Hoàn tất làm mới Market Radar." }] },
    network: { eyebrow: "Mạng lưới toàn cầu", title: "Mạng lưới vận hành từ Hàn Quốc đến Đông Nam Á.", body: "Kết nối sourcing, intelligence và phân phối qua các node khu vực.", tags: ["4 điểm vận hành cốt lõi", "Hàn Quốc + Đông Nam Á"], activeEyebrow: "Điểm đang chọn", logicLabel: "Logic khu vực", logicBody: "Mỗi node mang một vai trò vận hành riêng, giúp mạng lưới chuyển từ khám phá sản phẩm sang thực thi thị trường với trách nhiệm rõ ràng hơn." },
    operating: { eyebrow: "Tiêu chuẩn vận hành", title: "Được cấu trúc như một công ty vận hành quốc tế, không chỉ là nhà phân phối.", body: "QIANDU đang xây dựng một hệ thống mô-đun cho tăng trưởng mỹ phẩm xuyên biên giới, kết hợp cấu trúc thương mại, logic thị trường địa phương và thực thi dựa trên intelligence.", standardLabel: "Tiêu chuẩn" },
    workflow: { eyebrow: "Quy trình hợp tác", title: "Quy trình rõ ràng hơn cho hợp tác thương hiệu quốc tế và triển khai thị trường.", body: "Workflow của QIANDU được thiết kế để giảm mơ hồ từ cuộc trao đổi đầu tiên đến khi triển khai kênh.", stepLabel: "Bước" },
    insights: {
      eyebrow: "Tin tức",
      title: "Cập nhật từ QIANDU",
      body: "",
      cta: "Yêu cầu hồ sơ",
      ready: "Sẵn sàng",
      items: [
        {
          tag: "Tin doanh nghiệp",
          title: "Ngày 30 tháng 5 năm 2026, QIANDU GROUP phối hợp cùng Wishco và nghệ sĩ Thái Lan PEAK triển khai hoạt động retail activation cho JMsolution tại Bangkok.",
          body: "Hoạt động này tập trung vào tăng cường độ hiện diện bán lẻ và hỗ trợ mở rộng kênh offline cho JMsolution tại thị trường Bangkok.",
          date: "30/05/2026",
          image: "/company-news/peak-jmsolution-03.jpg",
        },
        {
          tag: "Cập nhật hợp tác",
          title: "Huong Giang chính thức trở thành đối tác truyền thông cho chiến dịch quảng bá nước tẩy trang MELOMELI của Hàn Quốc tại thị trường Việt Nam.",
          body: "Hợp tác này nhằm tăng cường độ nhận diện thương hiệu MELOMELI tại Việt Nam thông qua chiến dịch truyền thông bản địa hóa với người nổi tiếng.",
          date: "05/2026",
          image: "/company-news/melomeli-huonggiang-01.jpg",
        },
        { tag: "Công ty", title: "Danh mục K-Beauty của QIANDU tiếp tục được nâng cấp.", body: "" },
      ],
    },
    profile: { eyebrow: "Hồ sơ công ty", title: "Lớp trình bày sẵn sàng cho nhà phân phối, đối tác thương hiệu và nhà đầu tư.", body: "Mô-đun này chuẩn bị cho việc phát hành hồ sơ công ty, deck thương mại hoặc bộ hồ sơ năng lực.", preview: "Tài liệu công ty", documentTitle: "Hồ sơ công ty QIANDU", pdfReady: "Có thể cung cấp", request: "Yêu cầu hồ sơ", talk: "Trao đổi với QIANDU" },
    languages: { eyebrow: "Ngôn ngữ & phiên bản thị trường", title: "Thông điệp đa ngôn ngữ được thiết kế cho những cuộc trao đổi kinh doanh khu vực thực tế.", body: "Thay vì dịch từng chữ, mỗi phiên bản được định hình theo thị trường, đối tác và bối cảnh thương mại.", available: "Ngôn ngữ khả dụng", active: "Ngôn ngữ đang chọn", editionLabel: "Phiên bản", messagingLabel: "Thông điệp doanh nghiệp theo từng thị trường", focusTitle: "Trọng tâm bản địa hóa", releaseNote: "Ngôn ngữ đang được tinh chỉnh để tự nhiên hơn theo từng thị trường." },
    faq: { eyebrow: "FAQ / Phạm vi kinh doanh", title: "Những câu hỏi phổ biến từ đối tác và nhà phân phối quốc tế.", body: "Tóm tắt ngắn gọn cách QIANDU vận hành, tập trung vào đâu và mô hình hỗ trợ tăng trưởng khu vực như thế nào." },
    contact: { eyebrow: "Liên hệ & hợp tác", title: "Liên hệ với QIANDU.", body: "Dành cho hợp tác thương hiệu, sản phẩm và trao đổi thị trường khu vực.", formTitle: "Xem trước biểu mẫu hợp tác", responseLabel: "Phản hồi", responseValue: "Có cấu trúc", coverageLabel: "Phạm vi", coverageValue: "Hàn Quốc và APAC", fitLabel: "Phù hợp", fitValue: "Thương hiệu / Kênh", send: "Gửi yêu cầu", formNext: "Có thể liên hệ trực tiếp qua email", directReach: "Liên hệ trực tiếp", start: "Bắt đầu trao đổi", viewNetwork: "Xem mạng lưới", routingLabel: "Điều phối hợp tác", routingBody: "Phù hợp cho ủy quyền thương hiệu, hợp tác phân phối, dự án OEM và các cuộc trao đổi gia nhập thị trường dựa trên intelligence.", footnote: "Lộ trình phản hồi có cấu trúc cho các cuộc trao đổi về thương hiệu, phân phối, sourcing và vào thị trường" },
    footer: { summary: "Tạo ra giá trị cho khách hàng và cùng phát triển là giá trị cốt lõi của QIANDU.", pill: "Trụ sở đăng ký tại Incheon · Vận hành APAC · Công nghệ mỹ phẩm", quickLinksTitle: "Liên kết nhanh", languageTitle: "Ngôn ngữ", languageLine1: "English / 中文 / 한국어 / Tiếng Việt", languageLine2: "Chuyển ngôn ngữ toàn site", contactTitle: "Liên hệ", contactLocation: "Trụ sở đăng ký tại Incheon, Hàn Quốc\nVận hành sourcing và intelligence tại APAC", requestProfile: "Yêu cầu hồ sơ công ty", copyright: "© 2026 QIANDU. Bảo lưu mọi quyền.", tag: "Tạo giá trị, cùng phát triển" },
  },
};

function deepMerge<T>(base: T, override: any): T {
  if (Array.isArray(base)) {
    return (override ?? base) as T;
  }
  if (base && typeof base === "object") {
    const result: Record<string, unknown> = { ...(base as Record<string, unknown>) };
    const overrideObject = override ?? {};
    for (const key of Object.keys(overrideObject)) {
      result[key] = deepMerge((result as any)[key], overrideObject[key]);
    }
    return result as T;
  }
  return (override ?? base) as T;
}

export function getSiteCopy(locale: Locale) {
  if (locale === "en") {
    return baseEnglishCopy.en;
  }
  return deepMerge(baseEnglishCopy.en, localeOverrides[locale]);
}
